import { useCallback, useEffect, useRef, useState } from 'react';
import { lineStrings } from 'constants/strings';
import { ECardTypeTemplateId, TCardTypeCard } from 'types/cardTypeMessage';
import { cn } from 'utils/cn';
import { CardTypeMessagePreview } from './CardTypeMessagePreview';

const strings = lineStrings.management.cardTypeMessage;

type Props = {
  cards: TCardTypeCard[];
  variant?: 'editor' | 'inline';
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  onSelectCard?: (index: number) => void;
  className?: string;
  slideClassName?: string;
};

function CarouselNavButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'prev' | 'next';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={
        direction === 'prev' ? strings.carouselPrev : strings.carouselNext
      }
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'absolute top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/35 text-lg leading-none text-white shadow-md backdrop-blur-sm transition hover:bg-black/50 disabled:pointer-events-none disabled:opacity-0',
        direction === 'prev' ? 'left-0' : 'right-0',
      )}
    >
      {direction === 'prev' ? '‹' : '›'}
    </button>
  );
}

export function CardTypeMessagePicturesCarouselShell({
  cards,
  variant = 'editor',
  activeIndex: controlledIndex,
  onActiveIndexChange,
  onSelectCard,
  className,
  slideClassName,
}: Props) {
  const slideRefs = useRef<(HTMLElement | null)[]>([]);
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex !== undefined;
  const activeIndex = isControlled ? controlledIndex : internalIndex;

  const setActiveIndex = useCallback(
    (index: number) => {
      const next = Math.max(0, Math.min(cards.length - 1, index));
      if (!isControlled) {
        setInternalIndex(next);
      }
      onActiveIndexChange?.(next);
      onSelectCard?.(next);
    },
    [cards.length, isControlled, onActiveIndexChange, onSelectCard],
  );

  useEffect(() => {
    slideRefs.current[activeIndex]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [activeIndex]);

  if (cards.length === 0) return null;

  if (cards.length === 1) {
    const card = cards[0]!;
    return (
      <div
        data-testid="carousel-shell"
        className={cn('flex w-full justify-center', className)}
      >
        <CardTypeMessagePreview
          templateId={ECardTypeTemplateId.Pictures}
          card={card}
          variant={variant}
        />
      </div>
    );
  }

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < cards.length - 1;
  // Keep valid 15-character labels on one line in the narrowest phone preview.
  const defaultSlideClass =
    variant === 'inline'
      ? 'w-[78%] min-w-[188px] shrink-0 snap-center'
      : 'w-full max-w-[282px] shrink-0 snap-center';

  return (
    <div data-testid="carousel-shell" className={cn('relative w-full', className)}>
      <CarouselNavButton
        direction="prev"
        disabled={!canPrev}
        onClick={() => setActiveIndex(activeIndex - 1)}
      />
      <CarouselNavButton
        direction="next"
        disabled={!canNext}
        onClick={() => setActiveIndex(activeIndex + 1)}
      />

      <div
        className="flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {cards.map((card, index) => {
          const slideContent = (
            <CardTypeMessagePreview
              templateId={ECardTypeTemplateId.Pictures}
              card={card}
              variant={variant}
            />
          );

          const slideClass = cn(
            defaultSlideClass,
            slideClassName,
            variant === 'editor' &&
              (index === activeIndex
                ? 'rounded-lg ring-2 ring-accent ring-offset-2'
                : 'opacity-75'),
          );

          if (variant === 'editor') {
            return (
              <button
                key={index}
                ref={(el) => {
                  slideRefs.current[index] = el;
                }}
                type="button"
                data-testid="carousel-slide"
                aria-label={`${strings.cardTab} ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={() => setActiveIndex(index)}
                className={cn(slideClass, 'rounded-lg transition hover:opacity-100')}
              >
                {slideContent}
              </button>
            );
          }

          return (
            <div
              key={index}
              ref={(el) => {
                slideRefs.current[index] = el;
              }}
              data-testid="carousel-slide"
              className={slideClass}
            >
              {slideContent}
            </div>
          );
        })}
      </div>

      {variant === 'editor' ? (
        <p className="mt-2 text-center text-xs text-muted">
          {activeIndex + 1} / {cards.length}
        </p>
      ) : null}
    </div>
  );
}
